import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UPDATE_PROFILE, CHANGE_PASSWORD } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Moon, Sun, Bell, Zap, BarChart2, Sparkles, Smartphone, Mail,
  MessageSquare, Eye, Layers, ChevronRight, LogOut, Trash2,
  GraduationCap, BookOpen, Save, Camera, Shield, Settings, FilePen,
} from 'lucide-react';

/* ── Toggle ── */
function Toggle({ value, onChange }) {
  return (
    <button
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0
        ${value ? 'bg-brand-600' : 'bg-gray-200 dark:bg-surface-600'}`}
    >
      <span
        className={`absolute w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
          ${value ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

/* ── Settings row ── */
function SettingRow({ icon: Icon, iconGrad, label, sub, value, onChange, disabled }) {
  return (
    <div className={`flex items-center justify-between py-4 border-b border-gray-100 dark:border-white/[0.06] last:border-0
      ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: iconGrad }}>
          <Icon size={16} className="text-white" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
          {sub && <p className="text-xs text-gray-400 dark:text-white/40 mt-0.5">{sub}</p>}
        </div>
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

/* ── Section wrapper ── */
function SettingsSection({ title, children }) {
  return (
    <div className="card mb-4">
      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-white/30 mb-4 flex items-center gap-2">
        {title}
      </h3>
      {children}
    </div>
  );
}

/* ── Reusable profile field — defined outside ProfileSection so it keeps a stable
   reference across renders (inline component definitions cause focus loss on each keystroke) ── */
function ProfileField({ label, field, type = 'text', half = false, readOnly = false, editing, form, setForm }) {
  return (
    <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
      <label className="block text-xs font-medium text-gray-400 dark:text-white/40 mb-1">{label}</label>
      {editing && !readOnly ? (
        <input
          className="input"
          type={type}
          value={form[field]}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        />
      ) : (
        <p className="text-sm text-gray-900 dark:text-white font-medium py-2.5 px-4
          bg-gray-50 dark:bg-surface-700
          border border-gray-100 dark:border-white/[0.06]
          rounded-xl truncate">
          {form[field] || <span className="text-gray-300 dark:text-white/25">—</span>}
        </p>
      )}
    </div>
  );
}

/* ── Profile editor ── */
function ProfileSection({ user }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');
  const [form, setForm] = useState({
    name:     user?.name             || '',
    email:    user?.email            || '',
    branch:   user?.branch           || '',
    semester: user?.currentSemester  || user?.year || '',
    college:  user?.college          || '',
    rollNo:   user?.rollNo           || '',
  });
  const [updateProfile, { loading: saving }] = useMutation(UPDATE_PROFILE);

  const handleSave = async () => {
    setError(''); setSaved(false);
    try {
      const { data, errors } = await updateProfile({ variables: {
        name: form.name || undefined,
        branch: form.branch || undefined,
        year: form.semester ? Number(form.semester) : undefined,
        college: form.college || undefined,
      }});
      if (errors?.length) throw new Error(errors[0].message);
      const u = data?.updateProfile;
      if (u) {
        try {
          const stored = JSON.parse(localStorage.getItem('g2u_user') || '{}');
          localStorage.setItem('g2u_user', JSON.stringify({ ...stored, name: u.name, branch: u.branch, year: u.year, college: u.college }));
        } catch { /* non-fatal */ }
      }
      setEditing(false); setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save changes.');
    }
  };

  const initials = form.name
    ? form.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'CC';

  const fieldProps = { editing, form, setForm };

  return (
    <div className="card mb-4">
      {/* Avatar + name row */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-black text-white"
            style={{ background: '#1a73e8' }}
          >
            {initials}
          </div>
          {editing && (
            <button
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-600
                flex items-center justify-center
                border-2 border-white dark:border-surface-800
                hover:bg-brand-500 transition-colors"
              title="Upload photo (coming soon)"
            >
              <Camera size={13} className="text-white" />
            </button>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-black text-gray-900 dark:text-white truncate">
            {form.name || 'Your Name'}
          </h2>
          <p className="text-sm text-gray-400 dark:text-white/50 truncate">{form.email}</p>
          <span className="badge-brand mt-1 inline-block">Student</span>
        </div>
        <button
          onClick={() => setEditing(e => !e)}
          className={editing ? 'btn-outline btn-sm' : 'btn-brand btn-sm'}
        >
          {editing ? 'Cancel' : 'Edit Profile'}
        </button>
      </div>

      {/* Fields */}
      <div className="space-y-3">
        <ProfileField label="Full Name"           field="name"    {...fieldProps} />
        <ProfileField label="Email (sign-in ID, not editable)" field="email" type="email" readOnly {...fieldProps} />
        <div className="flex gap-3">
          <ProfileField label="Branch / Major"      field="branch"   half {...fieldProps} />
          <ProfileField label="Current Semester"    field="semester" half type="number" {...fieldProps} />
        </div>
        <ProfileField label="College / University" field="college" {...fieldProps} />
      </div>

      {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
      {saved && !editing && <p className="text-sm text-emerald-600 mt-3">✓ Profile saved</p>}

      {editing && (
        <div className="flex gap-3 mt-5">
          <button onClick={handleSave} disabled={saving} className="btn-gradient flex-1">
            <Save size={15} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Change password row ── */
function ChangePasswordRow() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [msg, setMsg]   = useState(null); // { ok, text }
  const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (form.next !== form.confirm) { setMsg({ ok: false, text: 'New passwords do not match.' }); return; }
    try {
      const { errors } = await changePassword({ variables: { currentPassword: form.current, newPassword: form.next } });
      if (errors?.length) throw new Error(errors[0].message);
      setMsg({ ok: true, text: '✓ Password updated' });
      setForm({ current: '', next: '', confirm: '' });
      setOpen(false);
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Could not change password.' });
    }
  };

  return (
    <div className="border-b border-gray-100 dark:border-white/[0.06]">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 group">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: '#1a73e8' }}>
            <Shield size={16} className="text-white" />
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Change Password</p>
            <p className="text-xs text-gray-400 dark:text-white/40 mt-0.5">Update your account password</p>
          </div>
        </div>
        <ChevronRight size={16} className={`text-gray-300 dark:text-white/30 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <form onSubmit={submit} className="pb-4 space-y-2">
          <input className="input" type="password" required placeholder="Current password"
            value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} />
          <input className="input" type="password" required minLength={8} placeholder="New password (min 8 characters)"
            value={form.next} onChange={e => setForm(f => ({ ...f, next: e.target.value }))} />
          <input className="input" type="password" required placeholder="Confirm new password"
            value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      )}
      {msg && <p className={`pb-3 text-sm ${msg.ok ? 'text-emerald-600' : 'text-red-500'}`}>{msg.text}</p>}
    </div>
  );
}

/* ── Main page ── */
export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { flags, toggleFlag, resetFlags } = useTheme();
  const navigate = useNavigate();
  const [showReset, setShowReset] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: '#1a73e8' }}>
          <Settings size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900 dark:text-white">Settings</h1>
          <p className="text-sm text-gray-400 dark:text-white/40">Profile, appearance &amp; preferences</p>
        </div>
      </div>

      {/* ── Profile ── */}
      <p className="section-title">Profile</p>
      <ProfileSection user={user} />

      {/* ── Appearance ── */}
      <p className="section-title">Appearance</p>
      <SettingsSection title="Theme &amp; Display">
        <SettingRow
          icon={flags.darkMode ? Moon : Sun}
          iconGrad="#1a73e8"
          label="Dark Mode"
          sub={flags.darkMode ? 'Switch to light theme' : 'Switch to dark theme'}
          value={flags.darkMode}
          onChange={() => toggleFlag('darkMode')}
        />
        <SettingRow
          icon={Zap}
          iconGrad="#b06000"
          label="Animations"
          sub="Slide, fade, and float effects throughout the app"
          value={flags.animationsEnabled}
          onChange={() => toggleFlag('animationsEnabled')}
        />
        <SettingRow
          icon={Layers}
          iconGrad="#1a73e8"
          label="Compact Sidebar"
          sub="Start with the sidebar collapsed by default"
          value={flags.compactSidebar}
          onChange={() => toggleFlag('compactSidebar')}
        />
        <SettingRow
          icon={Eye}
          iconGrad="#188038"
          label="Auto-expand Zones"
          sub="Open all navigation zones on app start"
          value={flags.autoExpandZones}
          onChange={() => toggleFlag('autoExpandZones')}
        />
      </SettingsSection>

      {/* ── Dashboard ── */}
      <p className="section-title">Dashboard</p>
      <SettingsSection title="Home Feed">
        <SettingRow
          icon={BarChart2}
          iconGrad="#1a73e8"
          label="Quick Stats"
          sub="Show CGPA, attendance, and subject count cards"
          value={flags.showQuickStats}
          onChange={() => toggleFlag('showQuickStats')}
        />
        <SettingRow
          icon={Sparkles}
          iconGrad="#1a73e8"
          label="New Features Strip"
          sub="Highlight recently added features on the dashboard"
          value={flags.showNewFeatures}
          onChange={() => toggleFlag('showNewFeatures')}
        />
      </SettingsSection>

      {/* ── Alerts ── */}
      <p className="section-title">Academic Alerts</p>
      <SettingsSection title="Academic Alerts">
        <SettingRow
          icon={GraduationCap}
          iconGrad="#1257a0"
          label="Attendance Warning"
          sub="Alert when attendance drops below 75%"
          value={flags.showAttendanceAlert}
          onChange={() => toggleFlag('showAttendanceAlert')}
        />
        <SettingRow
          icon={BarChart2}
          iconGrad="#b06000"
          label="CGPA Alert"
          sub="Alert when your CGPA falls below your set target"
          value={flags.showCgpaAlert}
          onChange={() => toggleFlag('showCgpaAlert')}
        />
      </SettingsSection>

      {/* ── Notifications ── */}
      <p className="section-title">Notifications</p>
      <SettingsSection title="Notification Channels">
        <SettingRow
          icon={Bell}
          iconGrad="#1a73e8"
          label="In-App Notifications"
          sub="Show notification badges and pop-ups inside the app"
          value={flags.notificationsEnabled}
          onChange={() => toggleFlag('notificationsEnabled')}
        />
        <SettingRow
          icon={MessageSquare}
          iconGrad="#188038"
          label="WhatsApp Alerts"
          sub="Receive booking confirmations and approvals via WhatsApp"
          value={flags.whatsappAlerts}
          onChange={() => toggleFlag('whatsappAlerts')}
        />
        <SettingRow
          icon={Mail}
          iconGrad="#1a73e8"
          label="Email Digest"
          sub="Daily summary of activity (coming soon)"
          value={flags.emailDigest}
          onChange={() => toggleFlag('emailDigest')}
          disabled
        />
      </SettingsSection>

      {/* ── Security ── */}
      <p className="section-title">Privacy &amp; Security</p>
      <div className="card mb-4">
        <ChangePasswordRow />
        <button className="w-full flex items-center justify-between py-4 group">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#F59E0B,#EF4444)' }}>
              <Smartphone size={16} className="text-white" />
            </span>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Two-Factor Auth</p>
              <p className="text-xs text-gray-400 dark:text-white/40 mt-0.5">Extra security layer (coming soon)</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-300 dark:text-white/30
            group-hover:text-gray-500 dark:group-hover:text-white/60 transition-colors" />
        </button>
      </div>

      {/* ── Advanced / Danger ── */}
      <p className="section-title">Advanced</p>
      <div className="card mb-4 border-red-100 dark:border-red-500/10">

        {/* Reset preferences */}
        <div className="py-4 border-b border-gray-100 dark:border-white/[0.06]">
          <button
            onClick={() => setShowReset(r => !r)}
            className="w-full flex items-center justify-between group"
          >
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-red-100 dark:bg-red-500/15">
                <Trash2 size={16} className="text-red-500" />
              </span>
              <div className="text-left">
                <p className="text-sm font-semibold text-red-600 dark:text-red-400">Reset Preferences</p>
                <p className="text-xs text-gray-400 dark:text-white/40 mt-0.5">Restore all flags to defaults</p>
              </div>
            </div>
            <ChevronRight size={16} className="text-gray-300 dark:text-white/30" />
          </button>

          {showReset && (
            <div className="mt-3 p-4 rounded-xl
              bg-red-50 dark:bg-red-500/10
              border border-red-200 dark:border-red-500/20">
              <p className="text-sm text-red-700 dark:text-red-300 mb-3 font-medium">
                This will reset dark mode, animations, alerts, and all other settings to their defaults.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { resetFlags(); setShowReset(false); }}
                  className="btn btn-sm bg-red-600 hover:bg-red-500 text-white"
                >
                  Yes, reset all
                </button>
                <button onClick={() => setShowReset(false)} className="btn-outline btn-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 py-4 group"
        >
          <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
            bg-gray-100 dark:bg-surface-700">
            <LogOut size={16} className="text-gray-400 dark:text-white/40
              group-hover:text-red-500 transition-colors" />
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-700 dark:text-white/70
              group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors">
              Sign Out
            </p>
            <p className="text-xs text-gray-400 dark:text-white/30 mt-0.5">You'll need to log back in</p>
          </div>
        </button>
      </div>

      <div className="text-center py-6 space-y-1">
        <p className="text-xs text-gray-400 dark:text-white/30">
          aura v2.0 · A{' '}
          <a
            href="https://quantaiseed.com"
            target="_blank"
            rel="noreferrer"
            className="text-brand-500 hover:text-brand-400 transition-colors"
          >
            Quantaiseed
          </a>{' '}
          product
        </p>
        <p className="text-xs text-gray-300 dark:text-white/20">
          Questions or feedback?{' '}
          <a
            href="mailto:contact@quantaiseed.com"
            className="text-brand-500 hover:text-brand-400 transition-colors"
          >
            contact@quantaiseed.com
          </a>
        </p>
      </div>
    </div>
  );
}
